import { MoreVertRounded } from "@mui/icons-material";
import { Chip, IconButton, Menu, MenuItem, Stack, SxProps, styled, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router";
import { ContextLabel, InlineTextField, IssueStatusPicker, LinkBehavior } from "..";
import { DEFAULT_ISSUE_STATUS, Links, Paths } from "../../constants";
import { useSession } from "../../hooks";
import { IssueSummaryDto, useDeleteIssue, useEditIssue, useRemoveIssueUpvote, useUpvoteIssue } from "../../http";
import { useDestructiveConfirm } from "../../utilities";
import { IssueIcons } from "./IssueIcons";

const StyledStack = styled(Stack)`
  cursor: pointer;

  .editMenuIcon {
    opacity: 0;
  }

  &:hover {
    .editMenuIcon {
      opacity: 1;
    }
  }
`;

interface IProps {
  issue: IssueSummaryDto;
  isFocused: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onBlur?: () => void;
  sx?: SxProps;
}

const Issue = (props: IProps) => {
  const { issue } = props;
  const user = useSession();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { mutate: editIssue } = useEditIssue();
  const { mutate: deleteIssue } = useDeleteIssue();
  const { mutate: upvoteIssue } = useUpvoteIssue();
  const { mutate: removeIssueUpvote } = useRemoveIssueUpvote();
  const theme = useTheme();
  const confirm = useDestructiveConfirm();
  const ref = useRef<HTMLInputElement>(null);
  const location = useLocation();

  // TODO: This will break in team meetings, so we'll have to figure out another way to address this
  const shouldShowTeamChips =
    !!matchPath(Paths.UserImplementation, location.pathname) || !!matchPath(Paths.Meeting, location.pathname);

  useEffect(() => {
    if (props.isFocused) {
      ref.current?.focus();
    }
  }, [props.isFocused]);

  async function handleDeleteClicked(e: React.MouseEvent) {
    setMenuAnchor(null);
    e.stopPropagation();

    const { confirmed } = await confirm({
      title: "Delete Issue",
      content: "Are you sure you want to delete this issue?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteIssue({
      issueGuid: issue.guid,
      teamSlug: issue.assignedTeam?.slug,
      createdByUserId: issue.createdByUser.userId,
    });
  }

  function handleIssueBlurred(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (issue.userCanEditTitle) {
      editIssue({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        description: e.target.value,
        createdByUserId: issue.createdByUser.userId,
      });
    }

    props.onBlur?.();
  }

  async function handleUpvoteClicked() {
    if (issue === undefined) {
      return;
    }

    if (issue.upvotes.some((x) => x.userId === user.userId)) {
      removeIssueUpvote({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
        userId: user.userId,
      });
    } else {
      upvoteIssue({
        issueGuid: issue.guid,
        teamSlug: issue.assignedTeam?.slug,
        createdByUserId: issue.createdByUser.userId,
        user: {
          userId: user.userId,
          displayName: user.displayName,
          enabled: true,
          profilePhotoSmallUrl: user.profilePhotoSmallUrl,
        },
      });
    }
  }

  return (
    <StyledStack
      direction="row"
      onClick={props.onClick}
      sx={{ alignItems: "center", py: 0, ...props.sx, width: "100%", minWidth: 0 }}
    >
      <Stack spacing={0.2} sx={{ py: 0.25, overflow: "hidden", width: "100%" }}>
        {shouldShowTeamChips && issue.assignedTeam && (
          <Stack direction="row" spacing={0.5} sx={{ pl: 1.75, pt: 0.5 }}>
            <Chip
              color="primary"
              component={LinkBehavior}
              label={issue.assignedTeam.name}
              href={Links.TeamImplementation(issue.assignedTeam.slug)}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              clickable
            />
            <Chip label="Issues" />
          </Stack>
        )}

        <Stack spacing={-0.25} sx={{ overflow: "hidden", width: "100%" }}>
          {issue.context && <ContextLabel context={issue.context} showIcon hideTeamName condensed sx={{ pl: 1.75 }} />}

          <Stack direction="row" sx={{ alignItems: "center", overflow: "hidden", width: "100%" }}>
            <IssueStatusPicker
              variant="button"
              issueGuid={issue.guid}
              status={issue.currentStatus ?? DEFAULT_ISSUE_STATUS}
              disabled={!issue.userCanEditOtherProperties}
              teamSlug={issue.assignedTeam?.slug}
              createdByUserGuid={issue.createdByUser.userId}
            />

            <InlineTextField
              ref={ref}
              value={issue.description}
              onClick={(e) => issue.userCanEditTitle && e.stopPropagation()}
              onBlur={handleIssueBlurred}
              placeholder="Enter item..."
              isEditable={issue.userCanEditTitle}
              maxLength={512}
            />
          </Stack>
        </Stack>

        <IssueIcons issue={issue} onUpvoteClicked={handleUpvoteClicked} sx={{ pl: 1.75, pb: 0.25 }} />
      </Stack>

      {issue.userCanEditOtherProperties && (
        <IconButton
          className="editMenuIcon"
          size="small"
          onClick={(e) => {
            setMenuAnchor(e.currentTarget);
            e.stopPropagation();
          }}
          sx={{ ml: 0.25 }}
        >
          <MoreVertRounded />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={(e: React.PointerEvent) => {
              setMenuAnchor(null);
              e.stopPropagation();
            }}
          >
            <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
              Delete Issue
            </MenuItem>
          </Menu>
        </IconButton>
      )}
    </StyledStack>
  );
};

export { Issue };
